import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "./Loader";
import Select from "react-select";
import axios from "axios";
import {
  IconIndustrialPollution,
  IconHazardousWaste,
  IconMoneyBag,
  IconPlasticWaste,
} from "./icons";
import "../Style/ProductComparisonSummar.css";
import StatsCard from "./atoms/StatsCard";

function ProductComparisonSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    currentState: {
      costTotal: "€0",
      co2eTotal: "0 kg",
      plasticTotal: "0 kg",
      hazardCodeScore: "N/A",
    },
    futureState: {
      costTotal: "€0",
      co2eTotal: "0 kg",
      plasticTotal: "0 kg",
      hazardCodeScore: "N/A",
    },
  });
  const [showComparison, setShowComparison] = useState(false);

  const { currentState, futureState } = location.state || {
    currentState: summaryData.currentState,
    futureState: summaryData.futureState,
  };
  const [differenceData, setDifferenceData] = useState({});

  useEffect(() => {
    axios
      .get("https://forethought.foremost-uk.com/api/latest-comparison")
      .then((response) => {
        setComparisonData(response.data.data);
      })
      .catch((error) => {
        console.error("Error fetching comparison data:", error);
      });

    fetchSummaryData();
  }, []);

  const fetchSummaryData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `https://forethought.foremost-uk.com/api/product-comparison-summary?timestamp=${new Date().getTime()}`
      );
      if (
        response.data &&
        response.data.total_price_1 !== undefined &&
        response.data.total_price_2 !== undefined
      ) {
        setSummaryData({
          currentState: {
            costTotal: `€${parseFloat(response.data.total_price_1).toFixed(0)}`,
            co2eTotal: `${response.data.total_co2_1.toFixed(0)} kg`,
            plasticTotal: `${response.data.total_plastic_1.toFixed(0)} kg`,
            hazardCodeScore: `${parseFloat(
              response.data.total_hazard_score_1
            ).toFixed(0)}`,
          },
          futureState: {
            costTotal: `€${parseFloat(response.data.total_price_2).toFixed(0)}`,
            co2eTotal: `${response.data.total_co2_2.toFixed(0)} kg`,
            plasticTotal: `${response.data.total_plastic_2.toFixed(0)} kg`,
            hazardCodeScore: `${parseFloat(
              response.data.total_hazard_score_2
            ).toFixed(0)}`,
          },
        });
      } else {
        throw new Error("Invalid data structure received from the API");
      }
    } catch (error) {
      console.error("Error fetching summary data:", error);
      alert("There was an error fetching summary data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (summaryData.currentState && summaryData.futureState) {
      const parsedDifferenceData = {
        costTotal: (
          parseNumeric(summaryData.currentState.costTotal) -
          parseNumeric(summaryData.futureState.costTotal)
        ).toFixed(2),
        costPercent: (
          (1 -
            parseNumeric(summaryData.futureState.costTotal) /
              parseNumeric(summaryData.currentState.costTotal)) *
          100
        ).toFixed(0),
        co2eTotal: (
          parseNumeric(summaryData.currentState.co2eTotal) -
          parseNumeric(summaryData.futureState.co2eTotal)
        ).toFixed(2),
        co2ePercent: (
          (1 -
            parseNumeric(summaryData.futureState.co2eTotal) /
              parseNumeric(summaryData.currentState.co2eTotal)) *
          100
        ).toFixed(0),
        plasticTotal: (
          parseNumeric(summaryData.currentState.plasticTotal) -
          parseNumeric(summaryData.futureState.plasticTotal)
        ).toFixed(2),
        plasticPercent: (
          (1 -
            parseNumeric(summaryData.futureState.plasticTotal) /
              parseNumeric(summaryData.currentState.plasticTotal)) *
          100
        ).toFixed(0),
        hazardCodeScore:
          parseNumeric(summaryData.currentState.hazardCodeScore) -
          parseNumeric(summaryData.futureState.hazardCodeScore),
        hazardPercent: (
          (1 -
            parseNumeric(summaryData.futureState.hazardCodeScore) /
              parseNumeric(summaryData.currentState.hazardCodeScore)) *
          100
        ).toFixed(0),
      };
      setDifferenceData(parsedDifferenceData);
    }
  }, [summaryData]);

  const parseNumeric = (value) =>
    parseFloat(value.replace(/[^\d.-]/g, "")) || 0;

  const handleImageClick = () => {
    if (showComparison) {
      const differenceData = {
        costTotal: (
          parseNumeric(summaryData.currentState.costTotal) -
          parseNumeric(summaryData.futureState.costTotal)
        ).toFixed(2),
        co2eTotal: (
          parseNumeric(summaryData.currentState.co2eTotal) -
          parseNumeric(summaryData.futureState.co2eTotal)
        ).toFixed(2),
        plasticTotal: (
          parseNumeric(summaryData.currentState.plasticTotal) -
          parseNumeric(summaryData.futureState.plasticTotal)
        ).toFixed(2),
        hazardCodeScore:(
          parseNumeric(summaryData.currentState.hazardCodeScore) -
          parseNumeric(summaryData.futureState.hazardCodeScore)
        ).toFixed(2)
      };
      navigate("/EP", { state: { differenceData } });
    } else {
      setShowComparison(true);
    }
  };

  const handleSkuChange = async (selectedSku, listId, id) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        "https://forethought.foremost-uk.com/api/update-sku",
        {
          list_id: listId,
          new_sku: selectedSku,
          id: id,
        }
      );
      console.log("SKU updated successfully:", response.data);
      console.log("Updating SKU", selectedSku, listId, id);
      fetchSummaryData();
    } catch (error) {
      console.error("Error updating SKU:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateMatchedApplication = async (selectedApplication, listId, id) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        "https://forethought.foremost-uk.com/api/update-application",
        {
          selectedApplication: selectedApplication.value,
          list_id: listId,
          id,
        }
      );
      console.log("Matched application updated successfully:", response.data);
      const updatedResponse = await axios.get(
        "https://forethought.foremost-uk.com/api/latest-comparison"
      );
      setComparisonData(updatedResponse.data.data);
      fetchSummaryData();
    } catch (error) {
      console.error("Error updating matched application:", error);
      alert(error.response ? error.response.data.error : error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="flex-container">
        <h1>Overview</h1>

        <div className="stats-container">
          <h2 className="grid-container-header">Current State</h2>
          <div className="grid-container">
            <StatsCard
              title="Cost Total"
              value={isLoading ? "Loading..." : summaryData.currentState.costTotal}
              icon={isLoading ? (<Loader />) : (<IconMoneyBag />)}
            />
            <StatsCard
              title="CO2e Total"
              value={isLoading ? "Loading..." : summaryData.currentState.co2eTotal}
              icon={isLoading ? (<Loader />) : (<IconIndustrialPollution />)}
            />
            <StatsCard
              title="Plastic Total"
              value={isLoading ? "Loading..." : summaryData.currentState.plasticTotal}
              icon={isLoading ? (<Loader />) : (<IconPlasticWaste />)}
            />
            <StatsCard
              title="Hazard Code Score"
              value={isLoading ? "Loading..." : summaryData.currentState.hazardCodeScore}
              icon={isLoading ? (<Loader />) : (<IconHazardousWaste />)}
            />
          </div>
        </div>
        <div className="stats-container">
          <h2 className="grid-container-header">Future State</h2>
          <div
            className="grid-container"
            style={{
              "--cardTextColor": "var(--color-black)",
              "--cardTextDetailColor": "var(--color-primary)",
              "--cardBgColor": "var(--color-secondary)",
            }}
          >
            <StatsCard
              title="Cost Total"
              value={isLoading ? "Loading..." : summaryData.futureState.costTotal}
              percent={isLoading ? "...%" : `${
                differenceData.costPercent >= 0 ? "↑" : "↓"
              } ${Math.abs(differenceData.costPercent || 0)}%`}
              icon={isLoading ? (<Loader />) : (<IconMoneyBag />)}
            />
            <StatsCard
              title="CO2e Total"
              value={isLoading ? "Loading..." : summaryData.futureState.co2eTotal}
              percent={isLoading ? "...%" : `${
                differenceData.co2ePercent >= 0 ? "↑" : "↓"
              } ${Math.abs(differenceData.co2ePercent || 0)}%`}
              icon={isLoading ? (<Loader />) : (<IconIndustrialPollution />)}
            />
            <StatsCard
              title="Plastic Total"
              value={isLoading ? "Loading..." : summaryData.futureState.plasticTotal}
              percent={isLoading ? "...%" : `${
                differenceData.plasticPercent >= 0 ? "↑" : "↓"
              } ${Math.abs(differenceData.plasticPercent || 0)}%`}
              icon={isLoading ? (<Loader />) : (<IconPlasticWaste />)}
            />
            <StatsCard
              title="Hazard Code Score"
              value={isLoading ? "Loading..." : summaryData.futureState.hazardCodeScore}
              percent={isLoading ? "...%" : `${
                differenceData.hazardPercent >= 0 ? "↑" : "↓"
              } ${Math.abs(differenceData.hazardPercent || 0)}%`}
              icon={isLoading ? (<Loader />) : (<IconHazardousWaste />)}
            />
          </div>
        </div>

        {showComparison && (
          <div className="comparison-table">
            <div className="table-headers">
              <h2>Current Product</h2>
              <h2>Application</h2>
              <h2>Future Product</h2>
            </div>

            {comparisonData.map((item, index) => (
              <div key={index} className="table-row">
                <div className="CurrentProduct">
                  <p>{item.description || "No description available"}</p>
                </div>

                {/* Application Dropdown */}
                <div>
                  <Select
                    placeholder="Select a matching Application"
                    defaultValue={
                      item.matched_application === "unmatched"
                        ? {
                            label: "Select a matching Application",
                            value: "",
                          }
                        : item.matchedApplications.length > 1
                        ? ""
                        : {
                            label: item.matchedApplications[0]?.label,
                            value: item.matchedApplications[0]?.value,
                          }
                    }
                    options={[
                      // Suggestions section if there are any similar applications
                      ...(item.similarDescriptions &&
                      item.similarDescriptions.length > 0
                        ? [
                            {
                              label: "Suggestions",
                              options: item.similarDescriptions.map((desc) => ({
                                label: desc.matched_application,
                                value: desc.matched_application,
                              })),
                            },
                          ]
                        : []),

                      // Full application list section (added `allApplications` for all available options)
                      {
                        label: "All Applications",
                        options: item.allApplications.map((app) => ({
                          label: app.label,
                          value: app.value,
                        })),
                      },
                    ]}
                    onChange={(selectedOption) =>
                      updateMatchedApplication(
                        selectedOption,
                        item.list_id,
                        item.id
                      )
                    }
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        margin: "0",
                        padding: "0",
                        minHeight: "auto",
                        color: "#7d7d7d",
                        backgroundColor: "transparent",
                        fontSize: "14px",
                        border: "none",
                        boxShadow: "none",
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        margin: "0",
                        padding: "0",
                      }),
                      singleValue: (baseStyles) => ({
                        ...baseStyles,
                        color: "#7d7d7d",
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        color: "#7d7d7d",
                        fontSize: "12px",
                      }),
                      indicatorSeparator: (baseStyles) => ({
                        ...baseStyles,
                        display: "none",
                      }),
                      dropdownIndicator: (baseStyles) => ({
                        ...baseStyles,
                        margin: "0",
                        padding: "0 0 0 8px",
                      }),
                      menuList: (baseStyles) => ({
                        ...baseStyles,
                        fontSize: "14px",
                        fontWeight: "400",
                        scrollbarWidth: "thin",
                      }),
                    }}
                  />
                </div>

                {/* Future Product Dropdown */}
                <div className="FutureProduct">
                  <select
                    defaultValue={
                      item.futureProductTitles[0]?.title === "unmatched"
                        ? "" // If the first option is "unmatched", default to an empty value
                        : item.futureProductTitles[0]?.sku // Otherwise, use the SKU value (not matched_sku_from/matched_sku_to)
                    }
                    onChange={(e) =>
                      handleSkuChange(
                        e.target.value,
                        item.list_id,
                        item.id,
                        item.futureProductTitles[0]?.title === "unmatched" // Check if the first item is unmatched
                      )
                    }
                  >
                    {/* Default disabled option for unmatched case */}
                    {item.futureProductTitles[0]?.title === "unmatched" && (
                      <option value="" disabled>
                        Select a Future Product
                      </option>
                    )}

                    {/* Render future product titles, excluding "unmatched" */}
                    {item.futureProductTitles.length > 0 ? (
                      item.futureProductTitles
                        .filter((titleObj) => titleObj.title !== "unmatched") // Filter out "unmatched"
                        .map((titleObj, titleIndex) => (
                          <option
                            key={titleIndex}
                            value={
                              titleObj.sku ||
                              titleObj.matched_sku_from ||
                              titleObj.matched_sku_to
                            }
                          >
                            {titleObj.title}
                          </option>
                        ))
                    ) : (
                      <option value="">Select a Matching Application</option>
                    )}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="footer">
          <button onClick={handleImageClick}>
            CONFIRM PRODUCTS & PURPOSES &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductComparisonSummary;
